import React, { ReactElement } from "react";
import { FormikStep } from "../FormikStepper";
import { object } from "yup";
import {
  attackDiceMap,
  damageTypes,
  DEFAULT_MEGA_REACTION,
  DEFAULT_REACTION,
  MonsterStats,
  getMonsterStatMap,
  PowerLevel,
  STRONG_MODIFICATIONS,
  WEAK_MODIFICATIONS,
  AttackTargets,
} from "../../../constants/Monster";
import { SliderField } from "../../common/form_fields/SliderField";
import { useFormikContext } from "formik";
import { STRING_VALIDATION } from "../../../constants/Forms";
import { SimpleGrid } from "@chakra-ui/react";
import { SelectField } from "../../common/form_fields/SelectField";
import { Break } from "../../styled/Break";
import { Hide } from "../../common/Hide";
import { SetState } from "../../../Types";
import { enumToString } from "../../../Utils";

const validationSchema1 = object().shape({
  name: STRING_VALIDATION,
  summary: STRING_VALIDATION,
  description: STRING_VALIDATION,
});

interface AttackProps {
  monsterStats: MonsterStats;
  setMonsterStats: SetState<MonsterStats>;
}

export function Attack({ monsterStats, setMonsterStats }: AttackProps): ReactElement {
  const formikProps = useFormikContext();

  const monsterStatMap = getMonsterStatMap();

  function adjustStats(newMonsterStats: MonsterStats, powerLevel: PowerLevel) {
    setMonsterStats(newMonsterStats);

    Object.entries(newMonsterStats).forEach((entry) => {
      const [key, value] = entry;
      if (key === "ability") {
        formikProps.setFieldValue("str", value.default);
        formikProps.setFieldValue("dex", value.default);
        formikProps.setFieldValue("int", value.default);
        formikProps.setFieldValue("wis", value.default);
        formikProps.setFieldValue("cha", value.default);
      } else {
        formikProps.setFieldValue(key, value.default);
      }
    });
    if (powerLevel === PowerLevel.Boss || powerLevel === PowerLevel.MegaBoss) {
      formikProps.setFieldValue("reactionDescription1", DEFAULT_MEGA_REACTION);
    } else {
      formikProps.setFieldValue("reactionDescription1", DEFAULT_REACTION);
    }
  }

  function getModifiationOptions(): string[] {
    return reachesPowerLevel(PowerLevel.Boss) ? STRONG_MODIFICATIONS : WEAK_MODIFICATIONS;
  }

  function onPowerLevelChange(value: string): void {
    switch (value) {
      case PowerLevel.Minion:
        adjustStats(monsterStatMap.get(PowerLevel.Minion)!, PowerLevel.Minion);
        break;
      case PowerLevel.Standard:
        adjustStats(monsterStatMap.get(PowerLevel.Standard)!, PowerLevel.Standard);
        break;
      case PowerLevel.Elite:
        adjustStats(monsterStatMap.get(PowerLevel.Elite)!, PowerLevel.Elite);
        break;
      case PowerLevel.Boss:
        adjustStats(monsterStatMap.get(PowerLevel.Boss)!, PowerLevel.Boss);
        break;
      case PowerLevel.MegaBoss:
        adjustStats(monsterStatMap.get(PowerLevel.MegaBoss)!, PowerLevel.MegaBoss);
        break;
    }
  }

  function reachesPowerLevel(requiredPowerLevel: PowerLevel): boolean {
    const currentPowerLevel: PowerLevel = formikProps.values["powerLevel"];

    switch (currentPowerLevel) {
      case PowerLevel.MegaBoss:
        return true;
      case PowerLevel.Boss:
        return requiredPowerLevel !== PowerLevel.MegaBoss;
      case PowerLevel.Elite:
        return ![PowerLevel.Boss, PowerLevel.MegaBoss].includes(requiredPowerLevel);
      case PowerLevel.Standard:
        return [PowerLevel.Standard, PowerLevel.Minion].includes(requiredPowerLevel);
      case PowerLevel.Minion:
        return requiredPowerLevel === PowerLevel.Minion;
    }
  }

  return (
    <FormikStep label="Attack" validationSchema={validationSchema1} submitDisabled={false}>
      <Break height="3vh" />
      <SelectField
        name="powerLevel"
        label=""
        borderColor="monster.secondary"
        borderHoverColor="monster.secondaryHover"
        // eslint-disable-next-line prettier/prettier
        options={[PowerLevel.Minion, PowerLevel.Standard, PowerLevel.Elite, PowerLevel.Boss, PowerLevel.MegaBoss]}
        callback={onPowerLevelChange}
        fontSize="1.5rem"
      />
      <Break height="5vh" />
      <SimpleGrid columns={{ lg: 3, md: 2, sm: 1 }} spacing="5vw">
        <SliderField
          label="Health"
          name="health"
          color="monster.primary"
          min={monsterStats.health.min}
          max={monsterStats.health.max}
          key="Slider Attack Health"
        />
        <SliderField
          label="Armor"
          name="armor"
          color="monster.primary"
          min={monsterStats.armor.min}
          max={monsterStats.armor.max}
          key="Slider Attack Armor"
        />
        <SliderField
          color="monster.primary"
          label="Speed"
          name="speed"
          min={monsterStats.speed.min}
          max={monsterStats.speed.max}
          step={5}
          key="Slider Attack Speed"
        />
      </SimpleGrid>
      <Hide hide={!reachesPowerLevel(PowerLevel.Standard)}>
        <SimpleGrid spacing="5vw" columns={{ lg: 2, sm: 1 }}>
          <SliderField
            label="Actions"
            name="actions"
            color="monster.primary"
            min={monsterStats.actions.min}
            max={monsterStats.actions.max}
            key="Slider Attack Actions"
          />
          <SliderField
            color="monster.primary"
            label={reachesPowerLevel(PowerLevel.Boss) ? "Mega-Reactions" : "Reactions"}
            name="reactions"
            min={monsterStats.reactions.min}
            max={monsterStats.reactions.max}
            key="Slider Reactions"
          />
        </SimpleGrid>
      </Hide>
      <Break height="5vh" />
      <SimpleGrid columns={{ xl: 5, lg: 3, md: 2, sm: 1 }} spacing="5vw">
        <SliderField
          name="str"
          color="monster.primary"
          min={monsterStats.ability.min}
          max={monsterStats.ability.max}
          label="Strength"
          key="Slider Attack Strength"
        />
        <SliderField
          name="dex"
          color="monster.primary"
          min={monsterStats.ability.min}
          max={monsterStats.ability.max}
          label="Agility"
          key="Slider Attack Dex"
        />
        <SliderField
          name="int"
          color="monster.primary"
          min={monsterStats.ability.min}
          max={monsterStats.ability.max}
          label="Intelligence"
          key="Slider Attack Int"
        />
        <SliderField
          name="wis"
          color="monster.primary"
          min={monsterStats.ability.min}
          max={monsterStats.ability.max}
          label="Wisdom"
          key="Slider Attack WIS"
        />
        <SliderField
          name="cha"
          color="monster.primary"
          min={monsterStats.ability.min}
          max={monsterStats.ability.max}
          label="Charisma"
          key="Slider Attack CHA"
        />
      </SimpleGrid>
      <Break height="5vh" />
      <SimpleGrid columns={{ lg: 2, sm: 1 }} spacing={"5vw"} justify="center">
        <SliderField
          label="Attack Dice Average"
          name="attackDice"
          color="monster.secondary"
          customTextFunction={() => attackDiceMap.get(formikProps.values["attackDice"])!}
          // marks={monsterStats.attackDice.marks.map((mark) => mark.value)}
          min={monsterStats.attackDice.min}
          max={monsterStats.attackDice.max}
          key="Slider Attack Dice"
          useTooltip={true}
        />
        <SliderField
          label="Attack Bonus"
          name="attackBonus"
          color="monster.secondary"
          min={monsterStats.attackBonus.min}
          max={monsterStats.attackBonus.max}
          key="Slider Attack Bonus"
        />
      </SimpleGrid>
      <SimpleGrid columns={{ lg: 4, sm: 2 }} spacing={"5vw"} justify="center">
        <SelectField
          label="Attack Type"
          name="attackTarget"
          borderColor="monster.secondary"
          borderHoverColor="monster.secondaryHover"
          options={enumToString(AttackTargets)}
        />
        <SelectField
          label="Damage Type"
          name="damageType"
          borderColor="monster.secondary"
          borderHoverColor="monster.secondaryHover"
          options={damageTypes}
        />
        <SliderField
          color="monster.secondary"
          label="Range"
          name="attackRange"
          step={5}
          min={monsterStats.attackRange.min}
          max={monsterStats.attackRange.max}
          key="Slider Attack Range"
        />
        <SliderField
          color="monster.secondary"
          label="Radius"
          name="attackRadius"
          step={5}
          min={monsterStats.attackRadius.min}
          max={monsterStats.attackRadius.max}
          key="Slider Attack Radius"
        />
      </SimpleGrid>
      <Break height="5vh" />
      <Hide hide={!reachesPowerLevel(PowerLevel.Standard)}>
        <SimpleGrid columns={{ lg: 2, sm: 1 }} spacing={"5vw"} justify="center">
          <SelectField
            options={getModifiationOptions()}
            name={"attackModifier1"}
            label={"Attack Modifications"}
            borderColor="monster.secondary"
            borderHoverColor="monster.secondaryHover"
          />
          <SliderField
            name={"attackModifierUses1"}
            label="Uses"
            min={1}
            max={reachesPowerLevel(PowerLevel.Boss) ? 10 : 5}
            color="monster.secondary"
          />
        </SimpleGrid>
        <Hide hide={!reachesPowerLevel(PowerLevel.Elite)}>
          <Break height="2vh" />
          <SimpleGrid columns={{ lg: 2, sm: 1 }} spacing={"5vw"} justify="center">
            <SelectField
              options={getModifiationOptions()}
              name={"attackModifier2"}
              label={""}
              borderColor="monster.secondary"
              borderHoverColor="monster.secondaryHover"
            />
            <SliderField name={"attackModifierUses2"} label="" min={1} max={reachesPowerLevel(PowerLevel.Boss) ? 10 : 5} color="monster.secondary" />
          </SimpleGrid>
        </Hide>
        <Hide hide={!reachesPowerLevel(PowerLevel.MegaBoss)}>
          <Break height="2vh" />
          <SimpleGrid columns={{ lg: 2, sm: 1 }} spacing={"5vw"} justify="center">
            <SelectField
              options={getModifiationOptions()}
              name={"attackModifier3"}
              label={""}
              borderColor="monster.secondary"
              borderHoverColor="monster.secondaryHover"
            />
            <SliderField name={"attackModifierUses3"} label="" min={1} max={reachesPowerLevel(PowerLevel.Boss) ? 10 : 5} color="monster.secondary" />
          </SimpleGrid>
        </Hide>
      </Hide>
    </FormikStep>
  );
}
